import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@/components/ui/button'
import { JobDescription } from '@/interfaces/jobDescription'
import { ReactionType } from '@/interfaces/reaction'
import { Images } from '@/assets/Images'
import { getRoleText, getReactionText } from '@/utils/helpers'
import { ReactionModal } from '@/components/ReactionModal'

interface TrendingJDSectionProps {
  trendingJDs: JobDescription[]
  onReact: (jobDescriptionId: string, reactionType: ReactionType) => void
  hideTitle?: boolean
}

const TrendingJDSection = ({ trendingJDs, onReact, hideTitle = false }: TrendingJDSectionProps) => {
  const navigate = useNavigate()
  const [selectedJD, setSelectedJD] = useState<JobDescription | undefined>(undefined)
  const [isModalOpen, setIsModalOpen] = useState(false)

  if (!trendingJDs || trendingJDs.length === 0) return null

  const handleReactionClick = (jd: JobDescription) => {
    setSelectedJD(jd)
    setIsModalOpen(true)
  }

  const getRankBadge = (index: number) => {
    const badges = {
      0: { image: Images.badge_top1, position: '-right-4 -top-2' },
      1: { image: Images.badge_top2, position: '-right-4 -top-2' },
      2: { image: Images.badge_top3, position: '-right-4 -top-2' },
    }
    return badges[index as keyof typeof badges] || badges[0]
  }

  // Mobile order: 1st, 2nd, 3rd (sequential)
  const mobileDisplayOrder = [
    { jd: trendingJDs[0], originalIndex: 0 },
    { jd: trendingJDs[1], originalIndex: 1 },
    { jd: trendingJDs[2], originalIndex: 2 },
  ].filter((item) => item.jd !== undefined)

  // Desktop order: 2nd, 1st, 3rd (podium style)
  const desktopDisplayOrder = [
    { jd: trendingJDs[1], originalIndex: 1 }, // 2nd place on left
    { jd: trendingJDs[0], originalIndex: 0 }, // 1st place in center
    { jd: trendingJDs[2], originalIndex: 2 }, // 3rd place on right
  ].filter((item) => item.jd !== undefined)

  const renderCard = (item: { jd: JobDescription; originalIndex: number }, isMobile: boolean) => {
    const { jd, originalIndex } = item
    if (!jd) return null
    const badge = getRankBadge(originalIndex)
    const isFirst = originalIndex === 0

    return (
      <div
        key={jd.id}
        className={`relative cursor-pointer ${
          isFirst && !isMobile ? 'md:mt-0 mt-8 md:scale-[1.3]' : 'md:mt-8'
        }`}
        onClick={() => navigate(`/jd/${jd.id}`)}
        style={{ maxWidth: '320px', width: '100%' }}
      >
        {/* Rank Badge */}
        <img
          src={badge.image}
          alt={`Top ${originalIndex + 1}`}
          className={`absolute ${badge.position} z-20 w-24 h-24`}
        />
        {/* Card Content */}
        <div
          className="p-6 pt-20 shadow-xl"
          style={{
            backgroundImage: `url(${Images.folder_background})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* JD Title */}
          <h4 className="font-momo-trust-display mb-2 text-xl font-bold text-black">
            Untold JD của
            <br />
            {jd.user?.name || 'MoMoer'}
          </h4>

          {/* Role */}
          <p className="mb-4 text-sm text-gray-700">{getRoleText(jd, false, true)}</p>

          {/* Reaction Text */}
          {getReactionText(jd) && (
            <div
              className="hover:text-gray-700 flex items-start gap-1 mb-3 text-xs text-gray-500 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                handleReactionClick(jd)
              }}
            >
              {getReactionText(jd)!.iconType === 'both' ? (
                <>
                  <img src={Images.icon_haha} alt="haha" className="w-4 h-4" />
                  <img src={Images.icon_heart} alt="heart" className="w-4 h-4" />
                </>
              ) : (
                <img
                  src={
                    getReactionText(jd)!.iconType === 'heart' ? Images.icon_heart : Images.icon_haha
                  }
                  alt={getReactionText(jd)!.iconType}
                  className="w-3 h-3"
                />
              )}
              <span className="text-xs">
                {getReactionText(jd)!.names.join(', ')}
                {getReactionText(jd)!.remaining > 0 && ` +${getReactionText(jd)!.remaining}`}
              </span>
            </div>
          )}

          {/* Reactions */}
          <div className="flex justify-center gap-4">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                onReact(jd.id, ReactionType.HEART)
              }}
              className="border border-primary flex items-center justify-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-colors bg-white hover:bg-[#FFEFF4] flex-1"
            >
              <img src={Images.icon_heart} alt="heart" className="w-5 h-5" />
              <span className="text-primary text-sm font-semibold">{jd.heartCount || 0}</span>
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation()
                onReact(jd.id, ReactionType.HAHA)
              }}
              className="border border-primary flex items-center justify-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-colors bg-white hover:bg-[#FFEFF4] flex-1"
            >
              <img src={Images.icon_haha} alt="haha" className="w-5 h-5" />
              <span className="text-primary text-sm font-semibold">{jd.hahaCount || 0}</span>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="rounded-3xl md:p-12 md:pb-25 p-8 mb-16"
      style={{
        backgroundImage: `url(${Images.background_paper})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
    >
      {/* Title - hidden when hideTitle is true */}
      {!hideTitle && (
        <div className="mb-20 text-center">
          <h3 className="md:text-3xl text-2xl font-bold text-black">Untold JD</h3>
          <h3 className="md:text-3xl text-2xl font-bold text-black">
            đang được <span className="text-primary">yêu thích</span>
          </h3>
        </div>
      )}

      {/* Mobile Cards Grid - Sequential order [1,2,3] */}
      <div className="md:hidden flex flex-wrap items-end justify-center gap-20">
        {mobileDisplayOrder.map((item) => renderCard(item, true))}
      </div>

      {/* Desktop Cards Grid - Podium order [2,1,3] */}
      <div className="md:flex md:flex-nowrap items-end justify-center hidden gap-20">
        {desktopDisplayOrder.map((item) => renderCard(item, false))}
      </div>

      {/* Reaction Modal */}
      <ReactionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        jobDescription={selectedJD}
      />
    </div>
  )
}

export default TrendingJDSection
